import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, MapPin, Navigation } from "lucide-react";
import { useLang } from "../../site-context";
import { LINKS } from "../../i18n";

const EASE = [0.22, 1, 0.36, 1];

export default function Contact() {
  const { t } = useLang();
  const actions = [
    {
      icon: Phone,
      title: LINKS.phoneIntl,
      label: t.contact_call_label,
      href: LINKS.phone,
      testid: "contact-phone-link",
      primary: true,
    },
    {
      icon: Phone,
      title: LINKS.phone2Intl,
      label: t.contact_call_label,
      href: LINKS.phone2,
      testid: "contact-phone2-link",
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      label: t.contact_whatsapp_label,
      href: LINKS.whatsapp,
      testid: "contact-whatsapp-link",
      whatsapp: true,
    },
    {
      icon: Mail,
      title: LINKS.emailDisplay,
      label: t.contact_email_label,
      href: LINKS.email,
      testid: "contact-email-link",
    },
  ];

  return (
    <section id="contactos" className="relative overflow-hidden py-24 lg:py-32" data-testid="contact-section">
      <div
        className="pointer-events-none absolute bottom-[-20%] left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-[#e3a008]/8 blur-[140px]"
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 max-w-2xl"
        >
          <p className="ff-mono mb-4 text-[11px] uppercase tracking-[0.35em] text-[#e3a008] sm:text-xs">
            {t.contact_eyebrow}
          </p>
          <h2 className="ff-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t.contact_title}
          </h2>
          <p className="mt-4 text-base font-light leading-relaxed text-slate-400 sm:text-lg">{t.contact_sub}</p>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {actions.map((a, i) => (
            <motion.a
              key={a.testid}
              href={a.href}
              target={a.whatsapp ? "_blank" : undefined}
              rel={a.whatsapp ? "noopener noreferrer" : undefined}
              data-testid={a.testid}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
              whileHover={{ y: -8, scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className={`group flex flex-col rounded-2xl border p-8 transition-colors duration-500 ${
                a.primary
                  ? "border-[#e3a008]/50 bg-[#e3a008] text-[#0a1120] shadow-[0_0_40px_rgba(227,160,8,0.3)]"
                  : a.whatsapp
                    ? "border-[#25D366]/40 bg-[#25D366]/10 text-white hover:bg-[#25D366]/15"
                    : "border-[#e3a008]/15 bg-[#12233f]/60 text-white hover:border-[#e3a008]/45"
              }`}
            >
              <a.icon
                className={`mb-6 h-8 w-8 ${
                  a.primary ? "text-[#0a1120]" : a.whatsapp ? "text-[#4be385]" : "text-[#e3a008]"
                }`}
                strokeWidth={1.75}
              />
              <span
                className={`ff-mono text-[10px] uppercase tracking-[0.3em] ${
                  a.primary ? "text-[#0a1120]/70" : "text-slate-400"
                }`}
              >
                {a.label}
              </span>
              <span className="ff-display mt-2 break-all text-xl font-semibold leading-tight sm:text-2xl">
                {a.title}
              </span>
            </motion.a>
          ))}
        </div>

        <motion.a
          href={LINKS.maps}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-map-card"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          className="group mt-5 flex flex-col items-start gap-6 rounded-2xl border border-[#e3a008]/15 bg-[#12233f]/60 p-8 transition-colors duration-500 hover:border-[#e3a008]/45 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-start gap-5">
            <div className="rounded-xl border border-[#e3a008]/25 bg-[#e3a008]/10 p-3.5">
              <MapPin className="h-6 w-6 text-[#e3a008]" strokeWidth={1.75} />
            </div>
            <div>
              <span className="ff-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
                {t.contact_address_title}
              </span>
              <p className="ff-display mt-1.5 text-xl font-semibold text-white sm:text-2xl">{t.contact_address}</p>
            </div>
          </div>
          <span className="flex items-center gap-2 rounded-full border border-[#e3a008]/40 px-5 py-2.5 text-sm font-medium text-[#e3a008] transition-colors duration-300 group-hover:bg-[#e3a008] group-hover:text-[#0a1120]">
            <Navigation className="h-4 w-4" />
            {t.contact_maps}
          </span>
        </motion.a>
      </div>
    </section>
  );
}
